const FIREBASE_AUTO_ID_PATTERN = /^[A-Za-z0-9]{20}$/;
const MAX_DATALOG_BYTES = 50 * 1024 * 1024;
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

export type CloudDatalogRoute =
  | { kind: 'none' }
  | { kind: 'invalid' }
  | { kind: 'datalog'; uploadId: string };

export type CloudDownloadProgress = (progress: number) => void;

type DownloadOptions = {
  apiUrl?: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
  onProgress?: CloudDownloadProgress;
  retryDelaysMs?: number[];
  wait?: (delayMs: number, signal?: AbortSignal) => Promise<void>;
};

export class CloudDatalogError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'CloudDatalogError';
    this.code = code;
  }
}

export function parseCloudDatalogRoute(pathname: string): CloudDatalogRoute {
  if (pathname === '/' || pathname === '') return { kind: 'none' };
  const match = /^\/log\/([^/]+)\/?$/.exec(pathname);
  if (!match) return pathname.startsWith('/log') ? { kind: 'invalid' } : { kind: 'none' };
  return FIREBASE_AUTO_ID_PATTERN.test(match[1])
    ? { kind: 'datalog', uploadId: match[1] }
    : { kind: 'invalid' };
}

function configuredApiUrl(apiUrl?: string): string {
  const value = apiUrl ?? import.meta.env.VITE_DATALOG_API_URL;
  if (!value) {
    throw new CloudDatalogError(
      'NOT_CONFIGURED',
      'Cloud datalog links are not configured for this deployment.',
    );
  }
  return value;
}

function decodeFileName(value: string | null): string {
  if (!value) {
    throw new CloudDatalogError('INVALID_RESPONSE', 'The cloud datalog response is incomplete.');
  }
  let fileName: string;
  try {
    fileName = decodeURIComponent(value).trim();
  } catch {
    throw new CloudDatalogError('INVALID_RESPONSE', 'The cloud datalog filename is invalid.');
  }
  if (!fileName || fileName.includes('/') || fileName.includes('\\') || !fileName.toLowerCase().endsWith('.csv')) {
    throw new CloudDatalogError('INVALID_RESPONSE', 'The cloud datalog filename is invalid.');
  }
  return fileName;
}

function parseExpectedSize(response: Response): number | null {
  const header = response.headers.get('Content-Length');
  if (header === null) return null;
  const size = Number(header);
  if (!Number.isSafeInteger(size) || size <= 0) {
    throw new CloudDatalogError('INVALID_RESPONSE', 'The cloud datalog size is invalid.');
  }
  if (size > MAX_DATALOG_BYTES) {
    throw new CloudDatalogError('TOO_LARGE', 'This release supports datalogs up to 50 MB.');
  }
  return size;
}

async function responseBlob(
  response: Response,
  expectedSize: number | null,
  onProgress?: CloudDownloadProgress,
): Promise<Blob> {
  if (!response.body) {
    const blob = await response.blob();
    onProgress?.(1);
    return blob;
  }

  const reader = response.body.getReader();
  const chunks: ArrayBuffer[] = [];
  let receivedBytes = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    receivedBytes += value.byteLength;
    if (receivedBytes > MAX_DATALOG_BYTES) {
      await reader.cancel();
      throw new CloudDatalogError('TOO_LARGE', 'This release supports datalogs up to 50 MB.');
    }
    const chunk = new Uint8Array(value.byteLength);
    chunk.set(value);
    chunks.push(chunk.buffer);
    if (expectedSize) onProgress?.(Math.min(0.99, receivedBytes / expectedSize));
  }
  onProgress?.(1);
  return new Blob(chunks, { type: 'text/csv' });
}

function waitForRetry(delayMs: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The cloud datalog download was canceled.', 'AbortError'));
      return;
    }
    const timer = window.setTimeout(resolve, delayMs);
    signal?.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('The cloud datalog download was canceled.', 'AbortError'));
      },
      { once: true },
    );
  });
}

async function requestDatalog(
  uploadId: string,
  apiUrl: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
  onProgress?: CloudDownloadProgress,
): Promise<File> {
  const url = new URL(apiUrl);
  url.searchParams.set('uploadId', uploadId);
  const response = await fetchImpl(url, { method: 'GET', signal });

  if (response.status === 503) {
    throw new CloudDatalogError('NOT_READY', 'The cloud datalog is still uploading.');
  }
  if (response.status === 413) {
    throw new CloudDatalogError('TOO_LARGE', 'This release supports datalogs up to 50 MB.');
  }
  if (response.status === 404) {
    throw new CloudDatalogError('NOT_FOUND', 'This cloud datalog is unavailable or the link is invalid.');
  }
  if (!response.ok) {
    throw new CloudDatalogError('DOWNLOAD_FAILED', 'The cloud datalog could not be downloaded.');
  }

  const fileName = decodeFileName(response.headers.get('X-Datalog-Filename'));
  const expectedSize = parseExpectedSize(response);
  const blob = await responseBlob(response, expectedSize, onProgress);
  if (blob.size === 0 || blob.size > MAX_DATALOG_BYTES || (expectedSize !== null && blob.size !== expectedSize)) {
    throw new CloudDatalogError('INVALID_RESPONSE', 'The cloud datalog download was incomplete.');
  }
  return new File([blob], fileName, { type: 'text/csv' });
}

export async function downloadCloudDatalog(uploadId: string, options: DownloadOptions = {}): Promise<File> {
  if (!FIREBASE_AUTO_ID_PATTERN.test(uploadId)) {
    throw new CloudDatalogError('INVALID_ID', 'This cloud datalog link is invalid.');
  }

  const apiUrl = configuredApiUrl(options.apiUrl);
  const fetchImpl = options.fetchImpl ?? fetch;
  const retryDelays = options.retryDelaysMs ?? RETRY_DELAYS_MS;
  const wait = options.wait ?? waitForRetry;

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await requestDatalog(uploadId, apiUrl, fetchImpl, options.signal, options.onProgress);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        throw new CloudDatalogError('CANCELED', 'The cloud datalog download was canceled.');
      }
      if (!(caught instanceof CloudDatalogError) || caught.code !== 'NOT_READY' || attempt >= retryDelays.length) {
        if (caught instanceof CloudDatalogError) throw caught;
        throw new CloudDatalogError('DOWNLOAD_FAILED', 'The cloud datalog could not be downloaded.');
      }
      options.onProgress?.(0);
      try {
        await wait(retryDelays[attempt], options.signal);
      } catch (waitError) {
        if (waitError instanceof DOMException && waitError.name === 'AbortError') {
          throw new CloudDatalogError('CANCELED', 'The cloud datalog download was canceled.');
        }
        throw waitError;
      }
    }
  }
}
